import React, {useEffect, useRef, useState} from 'react';
import {Card} from "../../../../components/card/card.jsx";
import {FaArrowLeft, FaCopy} from "react-icons/fa";
import Button from "../../../../components/button/button.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import AlertMessages from "../../../../components/messages/alert-message.js";
import myUserService from "../../../../services/web/my-user-service.js";
import {useQuery} from "@tanstack/react-query";
import QRCode from "react-qr-code";
import {toast} from "react-toastify";
import HandleHashInput from "../../../../components/handle-hash/input.js";
import HandleHashPaste from "../../../../components/handle-hash/paste.js";
import Alert from "../../../../components/alert/alert.jsx";
import {FaCircleExclamation} from "react-icons/fa6";

const MyUserTotpConfig = ({setTarget, refetch, setIsLoading}) => {
    const [showHash, setShowHash] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);

    useEffect(() => {
        return () => {
            setShowHash(false);
            setErrorMessage(undefined);
        }
    }, []);

    const hashRef = useRef([]);

    const {data} = useQuery({
        queryFn: () =>  myUserService.totpStart(),
        queryKey: ["config_totp_start"]
    });

    const formatSecretKey = () => {
        const secretKey = data?.secretKey;
        if (!secretKey) return "";

        const cleanedKey = secretKey.replace(/\s+/g, "");
        const groups = cleanedKey.match(/.{1,4}/g) || [];

        while (groups.length < 8) {
            groups.push("");
        }

        const firstLine = groups.slice(0, 4).join(' ');
        const secondLine = groups.slice(4, 8).join(' ');

        return firstLine + '\n' + secondLine;
    }

    const submit = () => {
        const hash = [...hashRef.current].map(el => el.value).join("");

        if (hash.length !== 6) {
            setErrorMessage("O código deve ter 6 dígitos.");
            return;
        }

        setIsLoading(true)

        const formData = new FormData();
        formData.append("hash", hash.toUpperCase());

        myUserService.totpValidate(formData)
            .then(() => {
                toast.success("Salvo com sucesso");
                setTarget("twoFactor");
                refetch();
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível prosseguir.";
                setTimeout(() => projectUtils.swalErrorMessage("", errMessage, "Ok"), 100);
            })
            .finally(() => setIsLoading(false))
    }

    return (
        <>
            <Card hidden={showHash}>
                <Card.Body>
                    <h3 className="flex gap-2 items-center">
                    <FaArrowLeft className="cursor-pointer font-normal" onClick={() => setTarget("twoFactor")}/>
                        Autenticação de dois fatores - Google Authenticator
                    </h3>

                    <div className="p-3">
                        <div className="grid md:grid-cols-2">
                            <div>
                                <span className="text-[16px] font-bold">Instruções para configuração</span>

                                <div className="mb-2">
                                    <span className="px-1 text-[14px] font-bold">1. Baixe um app de autenticação</span>
                                    <span className="px-2 text-[14px] block">
                                    &nbsp; Recomendamos que você baixe o Google Authenticator caso ainda não tenha um app instalado.
                                </span>
                                </div>
                                <div className="mb-2">
                                    <span className="px-1 text-[14px] font-bold">2. Faça a leitura do código de barras/QR code ou copie a chave</span>
                                    <span className="px-2 text-[14px] block">
                                    &nbsp; Faça a leitura deste código de barras/QR code no app de autenticação ou copie e cole a chave no app de autenticação.
                                </span>
                                </div>
                                <div className="mb-2">
                                    <span className="px-1 text-[14px] font-bold">3. Copie e insira o código de 6 dígitos</span>
                                    <span className="px-2 text-[14px] block">
                                    &nbsp; Depois que o código de barras/QR code for lido ou a chave for inserida, o app de autenticação gerará um código de 6 dígitos.
                                </span>
                                </div>
                            </div>

                            <div className="flex justify-between">
                                <div className="flex justify-center items-center">
                                    <QRCode
                                        size={150}
                                        value={data?.url ?? ""}
                                    />
                                </div>

                                <div className="flex flex-col items-center mx-3 align-items-center h-full justify-center">
                                    <div className="border-l border-l-[#4F4F4F] h-[50px]"></div>
                                    <span>ou</span>
                                    <div className="border-l border-l-[#4F4F4F] h-[50px]"></div>
                                </div>

                                <div className="flex flex-col justify-center items-center">
                                    <p className="text-[14px] font-semibold text-center mb-1">{formatSecretKey()}</p>
                                    <Button className="rounded-full" onClick={() => projectUtils.copyToClipboard(data?.secretKey ?? "")}>
                                        <FaCopy/> Copiar chave
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={() => setShowHash(true)}>
                                Avançar
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card hidden={!showHash}>
                <Card.Body>
                    <h3 className="text-center">
                        Autenticação de dois fatores - Google Authenticator
                    </h3>
                    <div className="text-center mb-4">
                        <span>
                            Insira o código de 6 dígitos gerado pelo
                        </span>
                        <br/>
                        <span className="text-[13px] font-bold">
                            Google Authenticator
                        </span>
                        <br/>
                    </div>

                    <div className="py-0 px-[60px]">
                        <div className="flex justify-center items-center mb-4">
                            <div className="flex flex-row gap-3">
                                {[...Array(6)].map((_, index) => (
                                    <input
                                        key={index}
                                        className="code form-control uppercase w-[35px] h-[35px] rounded-md px-2.5 border border-gray-300 focus:outline-[#1266f1]"
                                        type="text"
                                        maxLength="1"
                                        required
                                        autoFocus={index === 0}
                                        ref={(el) => hashRef.current[index] = el}
                                        onInput={(e) => HandleHashInput(e, index, hashRef)}
                                        onPaste={(e) => HandleHashPaste(e, hashRef)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {
                        !!errorMessage &&
                        <Alert target={"danger"}>
                            <FaCircleExclamation color={"#f27474"}/> {errorMessage}
                        </Alert>
                    }

                    <div className="flex justify-center mt-8">
                        <Button target="primary" className="w-1/4" onClick={submit} actionFeedbackType='save'>
                            Concluir
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default MyUserTotpConfig;